import { ShieldOutlined, VerifiedUser } from '@mui/icons-material';
import {
  CardContent,
  CardHeader,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material';
import { Card, Typography } from 'bso-ui';
import API from 'common/api';
import { PlaceHolderText } from 'components/PlaceholderCard/PlaceholderCard';
import Progress from 'components/Progress/Progress';
import SnackMessage from 'components/SnackMessage/SnackMessage';
import useConnectErrorHandler from 'helpers/errorHandlers';
import { allowActionOnUser } from 'helpers/permissions';
import { del, post } from 'helpers/query/fetchers';
import useList from 'hooks/useList';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';

const UserAssignGroup = ({ userData, refetchUser, loggedUserData }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { handleError } = useConnectErrorHandler();
  const [loadingGroupId, setLoadingGroupId] = useState(null);
  const userId = userData?.id;

  const query = {
    storeKey: 'company-list',
    apiEndpoint: API.COMPANY,
  };
  const [{
    data: companiesList, isLoading, isError, isFetched
  }] = useList(query.storeKey, query.apiEndpoint);

  const handleToggleGroup = async (event, group, isAdmin = false) => {
    const isChecked = event.target.checked;
    const { id: groupId, name: groupName } = group;
    setLoadingGroupId(groupId);
    const url = isAdmin
      ? API.SET_ADMIN(userId, groupId)
      : API.SET_GROUP(userId, groupId);
    let msg = '';
    try {
      if (isChecked) {
        await post(url);
        msg = isAdmin ? `User was made an admin of: ${groupName}.` : `User was added to company: ${groupName}`;
      } else {
        await del(url);
        msg = isAdmin ? `User is no longer an admin of: ${groupName}.` : `User was removed from company: ${groupName}`;
      }

      enqueueSnackbar(msg, {
        content: (key) => <SnackMessage id={key} message={msg} variant="success" />
      });
    } catch (err) {
      handleError(err);
    } finally {
      setLoadingGroupId(null);
      refetchUser();
    }
  };

  if (isError || (isFetched && !companiesList)) {
    return (
      <div style={{ height: '100px' }}>
        <PlaceHolderText variant="h3" content="Error getting companies" height="100%" />
      </div>
    );
  }

  const renderData = () => (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell><Typography variant="subtitle1">Company</Typography></TableCell>
          <TableCell><Typography variant="subtitle1">Member</Typography></TableCell>
          <TableCell><Typography variant="subtitle1">Admin</Typography></TableCell>
        </TableRow>
      </TableHead>
      {companiesList && companiesList?.length > 0
        ? (
          <TableBody>
            {companiesList.map((group) => {
              const isChecked = userData?.groups?.some((g) => g.name === group.name && !g.name.endsWith('_admins'));
              const isAdminChecked = userData?.groups?.some((g) => g.name === `${group.name}_admins`);
              const isGroupLoading = loadingGroupId === group.id;
              return (
                <TableRow
                  hover
                  key={group.id}
                >
                  <TableCell>
                    <Typography color="primary" variant="body2" component="span">
                      {group.name}
                    </Typography>
                  </TableCell>
                  {loggedUserData?.is_staff || allowActionOnUser([group], loggedUserData?.groups) ? (
                    <>
                      <TableCell>
                        <Checkbox
                          checked={isChecked}
                          onChange={(e) => handleToggleGroup(e, group)}
                          disabled={isGroupLoading}
                          data-cy={`group-${group.id}-member`}
                        />
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          icon={<ShieldOutlined />}
                          checkedIcon={<VerifiedUser />}
                          checked={isAdminChecked}
                          onChange={(e) => handleToggleGroup(e, group, true)}
                          disabled={isGroupLoading}
                          color="info"
                          data-cy={`group-${group.id}-admin`}
                        />
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <Tooltip title="You do not have permission on this group">
                        <TableCell>
                          <Checkbox
                            checked={isChecked}
                            disabled
                            data-cy={`group-${group.id}-member`}
                          />
                        </TableCell>
                      </Tooltip>
                      <Tooltip title="You do not have permission on this group">
                        <TableCell>
                          <Checkbox
                            icon={<ShieldOutlined />}
                            checkedIcon={<VerifiedUser />}
                            checked={isAdminChecked}
                            disabled
                            color="info"
                            data-cy={`group-${group.id}-admin`}
                          />
                        </TableCell>
                      </Tooltip>
                    </>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        )
        : null}
    </Table>

  );

  return (
    <Card>
      <CardHeader
        title={(
          <Typography variant="h4" color="primary">
            Assign companies
          </Typography>
            )}
      />
      <CardContent
        style={{
          paddingTop: 0,
        }}
      >
        {!isLoading
          ? renderData()
          : <Progress />}
      </CardContent>
    </Card>

  );
};

export default UserAssignGroup;
